import type { RuleId } from '@smart-link/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronsUpDown,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { z } from 'zod';
import { AuditDetailView } from '../components/AuditDetailView';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { api, getToken } from '../lib/api';
import { cn } from '../lib/cn';
import { RULE_LABEL, ruleLabel } from '../lib/rule-labels';
import { parseUa } from '../lib/ua';

interface LinkOption {
  id: string;
  name: string;
  status: string;
}

interface AuditRow {
  id: string;
  createdAt: number;
  decision: 'pass' | 'block' | 'pending';
  score: number;
  country: string | null;
  asn: number | null;
  asnOrg: string | null;
  ip: string | null;
  reasons: string;
  userAgent: string | null;
  isDatacenter: number;
  host: string | null;
}
interface ListResp {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface GroupRow {
  key: string | null;
  cnt: number;
  passCnt: number;
  blockCnt: number;
  lastAt?: number;
  /** 仅 by=device：该设备触达的不同链接数（跨链数） */
  distinctLinks?: number;
}

type TimeRange = '1h' | '24h' | 'today' | '7d' | 'all';
const RANGE_MS: Record<'1h' | '24h' | '7d', number> = {
  '1h': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
};

/** range==='today' 用北京时间自然日 0 点；其余用固定时间偏移；'all' 不限制 */
function rangeFromTs(range: TimeRange): number | undefined {
  if (range === 'all') return undefined;
  if (range === 'today') {
    const bj = 8 * 60 * 60_000;
    return Math.floor((Date.now() + bj) / 86_400_000) * 86_400_000 - bj;
  }
  return Date.now() - RANGE_MS[range];
}

type GroupBy = 'none' | 'asn' | 'ip' | 'country' | 'reason' | 'host' | 'device';
type ListSortKey = 'createdAt' | 'score';
type GroupSortKey = 'cnt' | 'passCnt' | 'blockCnt' | 'passRate' | 'lastAt' | 'key';
type SortDir = 'asc' | 'desc';

interface Filters {
  decision: '' | 'pass' | 'block' | 'pending';
  country: string;
  range: TimeRange;
  scoreMin: string;
  scoreMax: string;
  asn: string;
  ip: string;
  datacenter: 'all' | 'only' | 'hide';
  reason: string;
  host: string;
}

const DEFAULT_FILTERS: Filters = {
  decision: '',
  country: '',
  range: '24h',
  scoreMin: '',
  scoreMax: '',
  asn: '',
  ip: '',
  datacenter: 'all',
  reason: '',
  host: '',
};

/** 内容筛选是否全为默认（range 独立，不计入） */
function isClearedExceptRange(f: Filters): boolean {
  return (
    !f.decision &&
    !f.country &&
    !f.scoreMin &&
    !f.scoreMax &&
    !f.asn &&
    !f.ip &&
    f.datacenter === 'all' &&
    !f.reason &&
    !f.host
  );
}

// 快捷筛选：每个标签 toggle 一个/一组 filter 字段，可与高级筛选叠加；高亮按当前 filters 推断。
const QUICK_FILTERS: {
  id: string;
  label: string;
  isActive: (f: Filters) => boolean;
  toggle: (f: Filters) => Filters;
}[] = [
  {
    id: 'all',
    label: '全部',
    isActive: isClearedExceptRange,
    toggle: (f) => ({ ...DEFAULT_FILTERS, range: f.range }),
  },
  {
    id: 'pass',
    label: '仅通过',
    isActive: (f) => f.decision === 'pass',
    toggle: (f) => ({ ...f, decision: f.decision === 'pass' ? '' : 'pass' }),
  },
  {
    id: 'block',
    label: '仅拦截',
    isActive: (f) => f.decision === 'block',
    toggle: (f) => ({ ...f, decision: f.decision === 'block' ? '' : 'block' }),
  },
  {
    id: 'datacenter',
    label: '数据中心爬虫',
    isActive: (f) => f.datacenter === 'only',
    toggle: (f) => ({ ...f, datacenter: f.datacenter === 'only' ? 'all' : 'only' }),
  },
  {
    id: 'humanSuspect',
    label: '真人疑似',
    isActive: (f) => f.datacenter === 'hide' && f.scoreMin === '40',
    toggle: (f) =>
      f.datacenter === 'hide' && f.scoreMin === '40'
        ? { ...f, datacenter: 'all', scoreMin: '' }
        : { ...f, datacenter: 'hide', scoreMin: '40' },
  },
];

// 全字段 optional：导航到本路由时 search 可省略（读取处用 ?? 补默认）；
// catch(undefined) 兜底非法 URL 值，避免 parse 抛错白屏。
const auditsSearch = z.object({
  decision: z.enum(['pass', 'block', 'pending']).optional().catch(undefined),
  country: z.string().optional().catch(undefined),
  range: z.enum(['1h', '24h', 'today', '7d', 'all']).optional().catch(undefined),
  scoreMin: z.string().optional().catch(undefined),
  scoreMax: z.string().optional().catch(undefined),
  asn: z.string().optional().catch(undefined),
  ip: z.string().optional().catch(undefined),
  datacenter: z.enum(['all', 'only', 'hide']).optional().catch(undefined),
  reason: z.string().optional().catch(undefined),
  host: z.string().optional().catch(undefined),
  advanced: z
    .preprocess((v) => v === true || v === 'true', z.boolean())
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  groupBy: z
    .enum(['none', 'asn', 'ip', 'country', 'reason', 'host', 'device'])
    .optional()
    .catch(undefined),
  sortKey: z.enum(['createdAt', 'score']).optional().catch(undefined),
  sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
  groupSortKey: z
    .enum(['cnt', 'passCnt', 'blockCnt', 'passRate', 'lastAt', 'key'])
    .optional()
    .catch(undefined),
  groupSortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
});
type AuditsSearch = z.infer<typeof auditsSearch>;

// 等于默认值或空的键不写进 URL，保持地址栏干净
const SEARCH_DEFAULTS: Partial<AuditsSearch> = {
  range: '24h',
  datacenter: 'all',
  advanced: false,
  page: 1,
  groupBy: 'none',
  sortKey: 'createdAt',
  sortDir: 'desc',
  groupSortKey: 'cnt',
  groupSortDir: 'desc',
};

function stripDefaults(s: AuditsSearch): AuditsSearch {
  const out: Record<string, unknown> = {};
  const defaults = SEARCH_DEFAULTS as Record<string, unknown>;
  for (const [k, v] of Object.entries(s)) {
    if (v === undefined || v === '') continue;
    if (defaults[k] === v) continue;
    out[k] = v;
  }
  return out as AuditsSearch;
}

// Filters（含 '' 表示「全部」）→ URL search patch（'' 转 undefined 以清除该键）
function filtersToPatch(f: Filters): Partial<AuditsSearch> {
  return {
    decision: f.decision || undefined,
    country: f.country || undefined,
    range: f.range,
    scoreMin: f.scoreMin || undefined,
    scoreMax: f.scoreMax || undefined,
    asn: f.asn || undefined,
    ip: f.ip || undefined,
    datacenter: f.datacenter,
    reason: f.reason || undefined,
    host: f.host || undefined,
  } as Partial<AuditsSearch>;
}

export const Route = createFileRoute('/audits/$linkId')({
  validateSearch: (s: Record<string, unknown>): AuditsSearch => auditsSearch.parse(s),
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: AuditsPage,
});

function AuditsPage() {
  const { linkId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = useMemo<Filters>(
    () => ({
      decision: search.decision ?? '',
      country: search.country ?? '',
      range: search.range ?? '24h',
      scoreMin: search.scoreMin ?? '',
      scoreMax: search.scoreMax ?? '',
      asn: search.asn ?? '',
      ip: search.ip ?? '',
      datacenter: search.datacenter ?? 'all',
      reason: search.reason ?? '',
      host: search.host ?? '',
    }),
    [search],
  );
  const showAdvanced = search.advanced ?? false;
  const page = search.page ?? 1;
  const groupBy = search.groupBy ?? 'none';
  const listSort = useMemo(
    () => ({ key: search.sortKey ?? 'createdAt', dir: search.sortDir ?? 'desc' }),
    [search.sortKey, search.sortDir],
  );
  const groupSort = useMemo(
    () => ({ key: search.groupSortKey ?? 'cnt', dir: search.groupSortDir ?? 'desc' }),
    [search.groupSortKey, search.groupSortDir],
  );

  const setSearch = (patch: Partial<AuditsSearch>) => {
    navigate({ search: (prev) => stripDefaults({ ...prev, ...patch }) });
  };

  const linksQuery = useQuery({
    queryKey: ['links-options'],
    queryFn: () => api<{ rows: LinkOption[] }>('/api/links'),
    staleTime: 60_000,
  });

  const fromTs = useMemo(() => rangeFromTs(filters.range), [filters.range]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('linkId', linkId);
    if (groupBy === 'none') {
      p.set('page', String(page));
      p.set('pageSize', '50');
    }
    if (filters.decision) p.set('decision', filters.decision);
    if (filters.country) p.set('country', filters.country);
    if (fromTs) p.set('from', String(fromTs));
    if (filters.scoreMin) p.set('scoreMin', filters.scoreMin);
    if (filters.scoreMax) p.set('scoreMax', filters.scoreMax);
    if (filters.asn.trim()) p.set('asn', filters.asn.trim());
    if (filters.ip.trim()) p.set('ip', filters.ip.trim());
    if (filters.datacenter !== 'all') p.set('datacenter', filters.datacenter);
    if (filters.reason) p.set('reason', filters.reason);
    if (filters.host.trim()) p.set('host', filters.host.trim());
    if (groupBy === 'none') {
      p.set('sortBy', listSort.key);
      p.set('sortDir', listSort.dir);
    }
    return p;
  }, [linkId, filters, fromTs, page, groupBy, listSort]);

  const listQuery = useQuery({
    queryKey: ['audits', params.toString()],
    queryFn: () => api<ListResp>(`/api/audits?${params}`),
    enabled: groupBy === 'none',
  });

  const groupParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('linkId', linkId);
    p.set('by', groupBy === 'none' ? 'asn' : groupBy);
    if (filters.decision) p.set('decision', filters.decision);
    if (fromTs) p.set('from', String(fromTs));
    return p;
  }, [linkId, groupBy, filters.decision, fromTs]);

  const groupQuery = useQuery({
    queryKey: ['audits-groups', groupParams.toString()],
    queryFn: () => api<{ rows: GroupRow[] }>(`/api/audits/groups?${groupParams}`),
    enabled: groupBy !== 'none',
  });

  const applyQuick = (f: Filters) => {
    setSearch({ ...filtersToPatch(f), page: 1 });
  };

  const updateFilter = <K extends keyof Filters>(k: K, v: Filters[K]) => {
    setSearch({ [k]: v === '' ? undefined : v, page: 1 } as Partial<AuditsSearch>);
  };

  const linkOptions = linksQuery.data?.rows ?? [];

  const activeQuery = groupBy === 'none' ? listQuery : groupQuery;
  const refreshing = activeQuery.isFetching;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex items-baseline justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">审计记录</h1>
            <p className="mt-1 text-sm text-zinc-500">
              {groupBy === 'none'
                ? `共 ${listQuery.data?.total ?? '…'} 条`
                : `按${
                    groupBy === 'asn'
                      ? 'ASN'
                      : groupBy === 'ip'
                        ? 'IP'
                        : groupBy === 'country'
                          ? '国家'
                          : groupBy === 'host'
                            ? '域名'
                            : groupBy === 'device'
                              ? '跨链命中'
                              : '命中规则'
                  }分组，共 ${groupQuery.data?.rows.length ?? '…'} 组`}
            </p>
          </div>
          <LinkPicker
            links={linkOptions}
            value={linkId}
            onChange={(id) =>
              navigate({
                to: '/audits/$linkId',
                params: { linkId: id },
                search: (prev) => stripDefaults({ ...prev, page: 1 }),
              })
            }
          />
        </div>
        <ViewSwitcher groupBy={groupBy} onChange={(g) => setSearch({ groupBy: g, page: 1 })} />
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        {QUICK_FILTERS.map((p) => {
          const active = p.isActive(filters);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => applyQuick(p.toggle(filters))}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                active
                  ? 'border-zinc-900 bg-zinc-900 text-white dark:border-white dark:bg-white dark:text-zinc-900'
                  : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300',
              )}
            >
              {p.label}
            </button>
          );
        })}
        <select
          value={filters.range}
          onChange={(e) => updateFilter('range', e.target.value as TimeRange)}
          className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
        >
          <option value="1h">近 1 小时</option>
          <option value="24h">近 24 小时</option>
          <option value="today">今日</option>
          <option value="7d">近 7 天</option>
          <option value="all">全部时间</option>
        </select>
        <button
          type="button"
          onClick={() => setSearch({ advanced: !showAdvanced })}
          className="rounded-full border border-dashed border-zinc-300 px-3 py-1 text-xs text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900"
        >
          {showAdvanced ? '收起高级筛选 ▴' : '高级筛选 ▾'}
        </button>
        <button
          type="button"
          onClick={() => activeQuery.refetch()}
          disabled={refreshing}
          title="刷新最新记录"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs text-zinc-700 transition-colors hover:border-zinc-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-zinc-600"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
          刷新
        </button>
      </div>

      <ActiveFilterChips filters={filters} onPatch={(patch) => setSearch(patch)} />

      {showAdvanced && (
        <Card className="mb-4 p-4">
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Field label="判定">
              <select
                value={filters.decision}
                onChange={(e) => updateFilter('decision', e.target.value as Filters['decision'])}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="">全部</option>
                <option value="pass">仅通过</option>
                <option value="block">仅拦截</option>
                <option value="pending">未验证</option>
              </select>
            </Field>
            <Field label="国家(2 位代码)">
              <input
                value={filters.country}
                maxLength={2}
                onChange={(e) => updateFilter('country', e.target.value.toUpperCase())}
                placeholder="SG / US / CN"
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </Field>
            <Field label="ASN 包含">
              <input
                value={filters.asn}
                onChange={(e) => updateFilter('asn', e.target.value)}
                placeholder="Google / Amazon"
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </Field>
            <Field label="IP 包含">
              <input
                value={filters.ip}
                onChange={(e) => updateFilter('ip', e.target.value)}
                placeholder="34.x.x.x"
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </Field>
            <Field label="域名包含">
              <input
                value={filters.host}
                onChange={(e) => updateFilter('host', e.target.value)}
                placeholder="tt.cvnxkl.shop"
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </Field>
            <Field label="最低得分">
              <input
                type="number"
                value={filters.scoreMin}
                onChange={(e) => updateFilter('scoreMin', e.target.value)}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </Field>
            <Field label="最高得分">
              <input
                type="number"
                value={filters.scoreMax}
                onChange={(e) => updateFilter('scoreMax', e.target.value)}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              />
            </Field>
            <Field label="数据中心标记">
              <select
                value={filters.datacenter}
                onChange={(e) =>
                  updateFilter('datacenter', e.target.value as Filters['datacenter'])
                }
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="all">全部</option>
                <option value="only">仅数据中心</option>
                <option value="hide">隐藏数据中心</option>
              </select>
            </Field>
            <Field label="命中规则">
              <select
                value={filters.reason}
                onChange={(e) => updateFilter('reason', e.target.value)}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="">全部</option>
                {(Object.keys(RULE_LABEL) as RuleId[]).map((id) => (
                  <option key={id} value={id}>
                    {ruleLabel(id)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Card>
      )}

      {groupBy === 'none' ? (
        <ListView
          query={listQuery}
          linkId={linkId}
          fromSearch={search}
          page={page}
          onPageChange={(p) => setSearch({ page: p })}
          sort={listSort}
          onSort={(key) => {
            const dir = listSort.key === key ? (listSort.dir === 'desc' ? 'asc' : 'desc') : 'desc';
            setSearch({ sortKey: key, sortDir: dir, page: 1 });
          }}
        />
      ) : (
        <GroupView
          query={groupQuery}
          groupBy={groupBy}
          sort={groupSort}
          onSort={(key) => {
            const dir =
              groupSort.key === key ? (groupSort.dir === 'desc' ? 'asc' : 'desc') : 'desc';
            setSearch({ groupSortKey: key, groupSortDir: dir });
          }}
          onDrillDown={(key) => {
            // When user clicks a group row, switch to flat list filtered to that key
            const next = { ...filters };
            if (groupBy === 'asn') next.asn = key ?? '';
            else if (groupBy === 'ip') next.ip = key ?? '';
            else if (groupBy === 'country') next.country = (key ?? '').toUpperCase();
            else if (groupBy === 'reason') next.reason = key ?? '';
            else if (groupBy === 'host') next.host = key ?? '';
            setSearch({ ...filtersToPatch(next), groupBy: 'none', page: 1 });
          }}
        />
      )}
    </div>
  );
}

function LinkPicker({
  links,
  value,
  onChange,
}: {
  links: LinkOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const current = links.find((l) => l.id === value);
  const ql = q.trim().toLowerCase();
  const filtered = ql ? links.filter((l) => `${l.name} ${l.id}`.toLowerCase().includes(ql)) : links;
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQ('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-[300px] items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium dark:border-zinc-700 dark:bg-zinc-900"
        >
          <span className="truncate">
            {current ? `${current.name} (/${current.id})` : '选择链接'}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-0">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜索名称 / 短码"
              className="h-8 pl-8"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <div className="text-muted-foreground p-3 text-center text-xs">无匹配链接</div>
          )}
          {filtered.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => {
                onChange(l.id);
                setOpen(false);
                setQ('');
              }}
              className={cn(
                'hover:bg-accent flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                l.id === value && 'bg-accent',
              )}
            >
              <Check
                className={cn('size-3.5 shrink-0', l.id === value ? 'opacity-100' : 'opacity-0')}
              />
              <span className="min-w-0 flex-1 truncate">{l.name}</span>
              <code className="text-muted-foreground shrink-0 text-[10px]">/{l.id}</code>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ActiveFilterChips({
  filters,
  onPatch,
}: {
  filters: Filters;
  onPatch: (patch: Partial<AuditsSearch>) => void;
}) {
  const chips: { label: string; clear: Partial<AuditsSearch> }[] = [];
  if (filters.decision)
    chips.push({
      label: `判定: ${
        filters.decision === 'pass' ? '通过' : filters.decision === 'block' ? '拦截' : '未验证'
      }`,
      clear: { decision: undefined },
    });
  if (filters.datacenter !== 'all')
    chips.push({
      label: filters.datacenter === 'only' ? '仅数据中心' : '隐藏数据中心',
      clear: { datacenter: 'all' },
    });
  if (filters.scoreMin)
    chips.push({ label: `得分 ≥ ${filters.scoreMin}`, clear: { scoreMin: undefined } });
  if (filters.scoreMax)
    chips.push({ label: `得分 ≤ ${filters.scoreMax}`, clear: { scoreMax: undefined } });
  if (filters.country)
    chips.push({ label: `国家: ${filters.country}`, clear: { country: undefined } });
  if (filters.asn) chips.push({ label: `ASN: ${filters.asn}`, clear: { asn: undefined } });
  if (filters.ip) chips.push({ label: `IP: ${filters.ip}`, clear: { ip: undefined } });
  if (filters.host) chips.push({ label: `域名: ${filters.host}`, clear: { host: undefined } });
  if (filters.reason)
    chips.push({ label: `规则: ${ruleLabel(filters.reason)}`, clear: { reason: undefined } });
  if (chips.length === 0) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5">
      <span className="text-muted-foreground text-xs">已筛选:</span>
      {chips.map((c) => (
        <button
          key={c.label}
          type="button"
          onClick={() => onPatch({ ...c.clear, page: 1 })}
          className="bg-muted hover:bg-accent inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
        >
          {c.label}
          <X className="size-3" />
        </button>
      ))}
      <button
        type="button"
        onClick={() =>
          onPatch({
            decision: undefined,
            country: undefined,
            scoreMin: undefined,
            scoreMax: undefined,
            asn: undefined,
            ip: undefined,
            datacenter: 'all',
            reason: undefined,
            host: undefined,
            page: 1,
          })
        }
        className="text-muted-foreground hover:text-foreground text-xs underline"
      >
        清除全部
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-zinc-500">{label}</span>
      {children}
    </div>
  );
}

function ViewSwitcher({
  groupBy,
  onChange,
}: {
  groupBy: GroupBy;
  onChange: (g: GroupBy) => void;
}) {
  const options: { id: GroupBy; label: string }[] = [
    { id: 'none', label: '平铺' },
    { id: 'host', label: '按域名' },
    { id: 'asn', label: '按 ASN' },
    { id: 'ip', label: '按 IP' },
    { id: 'country', label: '按国家' },
    { id: 'reason', label: '按规则' },
    { id: 'device', label: '按跨链命中' },
  ];
  return (
    <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5 text-xs dark:border-zinc-700 dark:bg-zinc-900">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            'rounded px-2.5 py-1 transition-colors',
            groupBy === o.id
              ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
              : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SortHeader<K extends string>({
  label,
  sortKey,
  current,
  onClick,
  align = 'left',
}: {
  label: string;
  sortKey: K;
  current: { key: K; dir: SortDir };
  onClick: (k: K) => void;
  align?: 'left' | 'right';
}) {
  const active = current.key === sortKey;
  const Icon = !active ? ArrowUpDown : current.dir === 'desc' ? ArrowDown : ArrowUp;
  return (
    <th className={cn('px-4 py-2', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 transition-colors hover:text-zinc-900 dark:hover:text-zinc-100',
          active && 'text-zinc-900 dark:text-zinc-100',
        )}
      >
        {label}
        <Icon className="h-3 w-3 opacity-60" />
      </button>
    </th>
  );
}

function ListView({
  query,
  linkId,
  fromSearch,
  page,
  onPageChange,
  sort,
  onSort,
}: {
  query: { data?: ListResp; isLoading: boolean };
  linkId: string;
  fromSearch: AuditsSearch;
  page: number;
  onPageChange: (p: number) => void;
  sort: { key: ListSortKey; dir: SortDir };
  onSort: (k: ListSortKey) => void;
}) {
  const { data, isLoading } = query;
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <>
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/40">
              <tr>
                <SortHeader label="时间" sortKey="createdAt" current={sort} onClick={onSort} />
                <th className="px-4 py-2">判定</th>
                <SortHeader label="得分" sortKey="score" current={sort} onClick={onSort} />
                <th className="px-4 py-2">域名</th>
                <th className="min-w-[160px] px-4 py-2">设备/系统</th>
                <th className="px-4 py-2">国家</th>
                <th className="px-4 py-2">ASN</th>
                <th className="min-w-[180px] px-4 py-2">命中规则</th>
                <th className="sticky right-0 bg-zinc-50 px-4 py-2 text-right whitespace-nowrap shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)] dark:bg-zinc-900">
                  详情
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-zinc-500">
                    加载中…
                  </td>
                </tr>
              )}
              {data?.rows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-center text-zinc-500">
                    无匹配记录
                  </td>
                </tr>
              )}
              {data?.rows.map((r) => {
                return (
                  <tr
                    key={r.id}
                    className="border-t border-zinc-100 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900/40"
                  >
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-zinc-500">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      <Badge
                        variant={
                          r.decision === 'pass'
                            ? 'success'
                            : r.decision === 'pending'
                              ? 'info'
                              : 'destructive'
                        }
                      >
                        {r.decision === 'pending' ? '未验证' : r.decision}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 font-mono">{r.score}</td>
                    <td
                      className="max-w-[180px] truncate px-4 py-2 text-xs text-zinc-500"
                      title={r.host ?? ''}
                    >
                      {r.host ?? '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {(() => {
                        const ua = parseUa(r.userAgent);
                        if (!ua.os && !ua.browser) return <span className="text-zinc-400">-</span>;
                        const headless = /headless|electron|phantom/i.test(ua.browser ?? '');
                        return (
                          <div className="flex items-center gap-1">
                            {ua.os && (
                              <Badge variant="secondary" className="font-normal">
                                {ua.os}
                              </Badge>
                            )}
                            {ua.browser && (
                              <Badge
                                variant={headless ? 'destructive' : 'outline'}
                                className="font-normal"
                              >
                                {ua.browser}
                              </Badge>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-2">{r.country ?? '-'}</td>
                    <td className="px-4 py-2">
                      {r.asn ? (
                        <div className="flex max-w-[220px] items-center gap-1.5">
                          <code className="bg-muted shrink-0 rounded px-1 py-0.5 text-[10px] tabular-nums">
                            AS{r.asn}
                          </code>
                          <span
                            className="text-muted-foreground min-w-0 flex-1 truncate text-xs"
                            title={r.asnOrg ?? ''}
                          >
                            {r.asnOrg ?? '-'}
                          </span>
                          {r.isDatacenter ? (
                            <Badge variant="warning" className="shrink-0 text-[10px]">
                              机房
                            </Badge>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {(() => {
                        const all = JSON.parse(r.reasons) as string[];
                        if (all.length === 0)
                          return <span className="text-xs text-zinc-400">-</span>;
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex max-w-[200px] items-center gap-1.5 text-left text-xs text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
                              >
                                <span className="truncate">
                                  {all.slice(0, 2).map(ruleLabel).join('、')}
                                </span>
                                <span className="shrink-0 rounded bg-zinc-100 px-1 text-[10px] tabular-nums dark:bg-zinc-800">
                                  {all.length}
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-80">
                              <div className="mb-2 text-xs font-semibold">
                                命中规则 ({all.length})
                              </div>
                              <ul className="space-y-1.5 text-xs">
                                {all.map((id) => (
                                  <li key={id} className="flex items-start gap-2">
                                    <span className="bg-destructive mt-1 size-1.5 shrink-0 rounded-full" />
                                    <span>{ruleLabel(id)}</span>
                                  </li>
                                ))}
                              </ul>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </td>
                    <td className="bg-card sticky right-0 px-3 py-2 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={() => setOpenId(r.id)}
                      >
                        详情
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-zinc-500">
          第 {page} 页
          {data
            ? ` / 共 ${Math.max(1, Math.ceil(data.total / data.pageSize))} 页（${data.total} 条）`
            : ''}
        </span>
        <div className="flex items-center gap-1.5">
          {(() => {
            const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
            // 页码窗口：始终含首页、末页、当前页 ±1，其余折叠成省略号
            const set = new Set<number>([1, totalPages]);
            for (let p = page - 1; p <= page + 1; p++) {
              if (p >= 1 && p <= totalPages) set.add(p);
            }
            const pages = [...set].sort((a, b) => a - b);
            return (
              <>
                <button
                  type="button"
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="rounded-md border border-zinc-200 px-3 py-1 disabled:opacity-50 dark:border-zinc-800"
                >
                  上一页
                </button>
                {pages.map((p, i) => (
                  <Fragment key={p}>
                    {i > 0 && p - (pages[i - 1] ?? 0) > 1 && (
                      <span className="px-1 text-zinc-400">…</span>
                    )}
                    <button
                      type="button"
                      onClick={() => onPageChange(p)}
                      className={cn(
                        'min-w-8 rounded-md border px-2 py-1',
                        p === page
                          ? 'border-zinc-900 bg-zinc-900 text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900'
                          : 'border-zinc-200 dark:border-zinc-800',
                      )}
                    >
                      {p}
                    </button>
                  </Fragment>
                ))}
                <button
                  type="button"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="rounded-md border border-zinc-200 px-3 py-1 disabled:opacity-50 dark:border-zinc-800"
                >
                  下一页
                </button>
                <input
                  key={page}
                  type="number"
                  min={1}
                  max={totalPages}
                  defaultValue={page}
                  title="输入页码后回车跳转"
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    const v = Number(e.currentTarget.value);
                    if (v >= 1 && v <= totalPages) onPageChange(v);
                  }}
                  className="ml-1 w-14 rounded-md border border-zinc-200 px-2 py-1 text-center dark:border-zinc-800 dark:bg-zinc-900"
                />
              </>
            );
          })()}
        </div>
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-[92vw] sm:max-w-4xl">
          <SheetHeader className="shrink-0 border-b">
            <SheetTitle>审计详情</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-6">
            {openId && <AuditDetailView id={openId} />}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function GroupView({
  query,
  groupBy,
  sort,
  onSort,
  onDrillDown,
}: {
  query: { data?: { rows: GroupRow[] }; isLoading: boolean };
  groupBy: GroupBy;
  sort: { key: GroupSortKey; dir: SortDir };
  onSort: (k: GroupSortKey) => void;
  onDrillDown: (key: string | null) => void;
}) {
  const { data, isLoading } = query;
  const labelByKind: Record<Exclude<GroupBy, 'none'>, string> = {
    asn: 'ASN',
    ip: 'IP',
    country: '国家',
    reason: '命中规则',
    host: '域名',
    device: '设备',
  };
  const colLabel = labelByKind[groupBy as Exclude<GroupBy, 'none'>];

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const arr = [...data.rows];
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (sort.key) {
        case 'key':
          av = String(a.key ?? '');
          bv = String(b.key ?? '');
          return av.localeCompare(bv) * dirMul;
        case 'passRate':
          av = a.cnt > 0 ? a.passCnt / a.cnt : 0;
          bv = b.cnt > 0 ? b.passCnt / b.cnt : 0;
          break;
        case 'lastAt':
          av = a.lastAt ?? 0;
          bv = b.lastAt ?? 0;
          break;
        default:
          av = (a[sort.key] as number) ?? 0;
          bv = (b[sort.key] as number) ?? 0;
      }
      return ((av as number) - (bv as number)) * dirMul;
    });
    return arr;
  }, [data?.rows, sort]);

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/40">
            <tr>
              <SortHeader label={colLabel} sortKey="key" current={sort} onClick={onSort} />
              <SortHeader
                label="总数"
                sortKey="cnt"
                current={sort}
                onClick={onSort}
                align="right"
              />
              <SortHeader
                label="通过"
                sortKey="passCnt"
                current={sort}
                onClick={onSort}
                align="right"
              />
              <SortHeader
                label="拦截"
                sortKey="blockCnt"
                current={sort}
                onClick={onSort}
                align="right"
              />
              <SortHeader
                label="通过率"
                sortKey="passRate"
                current={sort}
                onClick={onSort}
                align="right"
              />
              <SortHeader label="最近一次" sortKey="lastAt" current={sort} onClick={onSort} />
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-zinc-500">
                  加载中…
                </td>
              </tr>
            )}
            {sortedRows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-zinc-500">
                  无数据
                </td>
              </tr>
            )}
            {sortedRows.map((r) => {
              const total = r.cnt;
              const passRate = total > 0 ? ((r.passCnt / total) * 100).toFixed(0) : '0';
              const label =
                groupBy === 'reason'
                  ? ruleLabel(r.key ?? '')
                  : groupBy === 'device'
                    ? r.key
                      ? `${r.key.slice(0, 12)}…`
                      : '未知设备'
                    : (r.key ?? '未知');
              return (
                <tr
                  key={r.key ?? '_null'}
                  className="border-t border-zinc-100 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900/40"
                >
                  <td className="px-4 py-2">
                    <span
                      className={cn('font-medium', groupBy === 'device' && 'font-mono text-xs')}
                    >
                      {label}
                    </span>
                    {groupBy === 'device' && r.distinctLinks != null && (
                      <Badge variant="warning" className="ml-2 text-[10px]">
                        跨 {r.distinctLinks} 链接
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{total}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-600">{r.passCnt}</td>
                  <td className="px-4 py-2 text-right font-mono text-red-600">{r.blockCnt}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-500">{passRate}%</td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-zinc-500">
                    {r.lastAt ? new Date(r.lastAt).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {groupBy !== 'device' && (
                      <button
                        type="button"
                        onClick={() => onDrillDown(r.key)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        查看详情 →
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
